import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppBar, Box, IconButton, LinearProgress, styled, Typography, useTheme } from "@mui/material";
import { getStatementOptions } from "../../../../redux/statementOptionsSlice";
import { DataGridPremium, GRID_DETAIL_PANEL_TOGGLE_FIELD, gridPivotModelSelector, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter, gridClasses } from "@mui/x-data-grid-premium";
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
    //   DataGrid,
    Toolbar,
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger,
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger,
} from '@mui/x-data-grid';

const StyledQuickFilter = styled(QuickFilter)({
    display: 'grid',
    alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    overflowX: 'clip',
    width: ownerState.expanded ? 260 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity']),
    '& .MuiInputBase-root': {
        height: '30px',
    },
    '& .MuiInputBase-input': {
        fontSize: '14px',
    }
}));

const highlightColor = '#ffcccc'; // สีไฮไลต์ (สีชมพูอ่อน)
const duplicatedDetailRowHeight = 34;
const duplicatedDetailHeaderHeight = 34;
const duplicatedDetailSectionTitleHeight = 28;
const duplicatedDetailSectionSpacing = 12;
const duplicatedDetailPanelBasePadding = 24;
const duplicatedDetailPanelMinHeight = 132;
const duplicatedDetailPanelMaxHeight = 220;
const EMPTY_EXPANDED_ROW_IDS = new Set();

const normalizeRows = (rows) => {
    if (Array.isArray(rows)) {
        return rows.filter((row) => row && typeof row === 'object');
    }

    if (rows && typeof rows === 'object') {
        return [rows];
    }

    return [];
};

const normalizeDuplicatedEntries = (entries) => {
    if (Array.isArray(entries)) {
        return entries.filter((entry) => entry && typeof entry === 'object');
    }

    if (entries && typeof entries === 'object') {
        return [entries];
    }

    return [];
};

const buildDuplicatedIndexMap = (entries, totalRows) => {
    if (!entries || entries.length === 0 || totalRows === 0) {
        return new Map();
    }

    const rawIndexGroups = entries.map((entry) => (
        Array.isArray(entry?.duplicate_indexes)
            ? entry.duplicate_indexes
                .map((value) => Number(value))
                .filter((value) => Number.isInteger(value))
            : (entry?.duplicate_indexes !== undefined && entry?.duplicate_indexes !== null)
                ? [entry.duplicate_indexes]
                    .map((value) => Number(value))
                    .filter((value) => Number.isInteger(value))
                : []
    ));

    const allRawIndexes = rawIndexGroups.flat();
    const inRangeZeroBased = allRawIndexes.filter((value) => value >= 0 && value < totalRows).length;
    const inRangeOneBased = allRawIndexes.filter((value) => value >= 1 && value <= totalRows).length;
    const shouldConvertOneBasedToZeroBased = totalRows > 0 && inRangeOneBased > inRangeZeroBased;

    const normalizeIndex = (value) => shouldConvertOneBasedToZeroBased ? value - 1 : value;

    const map = new Map();
    rawIndexGroups.forEach((rawIndexes) => {
        const indexes = [...new Set(rawIndexes
            .map((value) => normalizeIndex(value))
            .filter((value) => value >= 0 && value < totalRows))];

        if (indexes.length === 1) {
            const onlyIndex = indexes[0];
            const existingPeers = map.get(onlyIndex) || [];

            if (existingPeers.length === 0) {
                // Keep singleton groups expandable when backend returns a single duplicate index.
                map.set(onlyIndex, [onlyIndex]);
            }
        }

        indexes.forEach((baseIndex) => {
            const peers = indexes.filter((index) => index !== baseIndex);
            if (peers.length === 0) return;

            const existingPeers = map.get(baseIndex) || [];
            const mergedPeers = [...new Set([...existingPeers, ...peers])].sort((a, b) => a - b);
            map.set(baseIndex, mergedPeers);
        });
    });

    return map;
};

const DataGridSimulate = (props) => {
    const { selectedStatement, isLoadingTable, dataSourceSimulate } = props;
    const theme = useTheme();
    const statementOptions = useSelector(getStatementOptions);
    const [noData, setNoData] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(new Set());
    const executeTime = Number(dataSourceSimulate?.execute_time || 0);
    const normalizedDataRows = useMemo(() => normalizeRows(dataSourceSimulate?.data), [dataSourceSimulate?.data]);
    const duplicatedEntries = useMemo(() => (
        normalizeDuplicatedEntries(dataSourceSimulate?.duplicated_data?.duplicated)
    ), [dataSourceSimulate?.duplicated_data?.duplicated]);
    const overlapDuplicatedEntries = useMemo(() => (
        normalizeDuplicatedEntries(dataSourceSimulate?.duplicated_data?.overlap_duplicated)
    ), [dataSourceSimulate?.duplicated_data?.overlap_duplicated]);
    const isSingleStatementSelection = statementOptions.length === 1
        && (selectedStatement === null || selectedStatement === undefined || selectedStatement === '' || selectedStatement === statementOptions[0]);
    const isDuplicatedViewEnabled = selectedStatement === 'All' || isSingleStatementSelection;
    const totalRows = normalizedDataRows.length;

    const duplicatedIndexMap = useMemo(() => {
        return buildDuplicatedIndexMap(duplicatedEntries, totalRows);
    }, [duplicatedEntries, totalRows]);

    const overlapDuplicatedIndexMap = useMemo(() => {
        return buildDuplicatedIndexMap(overlapDuplicatedEntries, totalRows);
    }, [overlapDuplicatedEntries, totalRows]);

    const duplicatedRowIdSet = useMemo(() => {
        return new Set([
            ...Array.from(duplicatedIndexMap.keys()),
            ...Array.from(overlapDuplicatedIndexMap.keys()),
        ]);
    }, [duplicatedIndexMap, overlapDuplicatedIndexMap]);

    const normalizeCountDuplicated = (duplicated) => {
        const excessDuplicateCount = duplicated.reduce(
            (total, item) => total + Number(item.duplicate_count),
            0
        );
        return excessDuplicateCount;
    };

    const columns = useMemo(() => {
        if (normalizedDataRows.length === 0) return [];

        // ดึง keys ทั้งหมดจาก object ตัวแรก แล้ว sort เพื่อให้ 'statement' อยู่ด้านหน้า
        const keys = Object.keys(normalizedDataRows[0]);
        const sortedKeys = keys.sort((a, b) => {
            // ให้ 'statement' อยู่ลำดับแรก
            if (a === 'statement') return -1;
            if (b === 'statement') return 1;
            return 0;
        });

        const dataColumns = sortedKeys.map((key) => {
            return {
                field: key,
                headerName: key,
                width: 150,
                valueFormatter: (value) => {
                    return value === null ? 'null' : value;
                },
            };
        });

        const outputColumns = [
            {
                field: '__row_index__',
                headerName: 'index',
                width: 75,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                valueGetter: (_value, row) => Number(row?.id),
            },
            ...dataColumns,
        ];

        if (isDuplicatedViewEnabled) {
            outputColumns.unshift({
                field: '__duplicate_toggle__',
                headerName: '',
                width: 44,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                disableExport: true,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const rowId = Number(params.id);
                    if (!duplicatedRowIdSet.has(rowId)) {
                        return null;
                    }

                    const isExpanded = detailPanelExpandedRowIds.has(rowId);
                    return (
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                setDetailPanelExpandedRowIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(rowId)) {
                                        next.delete(rowId);
                                    } else {
                                        next.add(rowId);
                                    }
                                    return next;
                                });
                            }}
                        >
                            {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                        </IconButton>
                    );
                },
            });
        }

        return outputColumns;
    }, [normalizedDataRows, isDuplicatedViewEnabled, duplicatedRowIdSet, detailPanelExpandedRowIds]);

    const rowsWithId = useMemo(() => {
        if (normalizedDataRows.length === 0) return [];
        return normalizedDataRows.map((row, index) => ({
            ...row,
            id: index, // ใช้ index ของ array เป็น id (ใช้ได้ดีกับข้อมูลที่อ่านอย่างเดียว)
        }));
    }, [normalizedDataRows]);

    const rowsWithIdMap = useMemo(() => {
        const map = new Map();
        rowsWithId.forEach((row) => {
            map.set(Number(row.id), row);
        });
        return map;
    }, [rowsWithId]);

    const filteredData = useMemo(() => {
        if (!selectedStatement && rowsWithId.length === 0) return [];
        if (!selectedStatement && statementOptions.length === 1 && rowsWithId.length > 0) return rowsWithId;
        if (selectedStatement === 'All') return rowsWithId;
        return rowsWithId.filter(item => item.statement === selectedStatement);
    }, [selectedStatement, rowsWithId, statementOptions]);

    const detailPanelColumns = useMemo(() => {
        return [
            {
                field: '__source_index__',
                headerName: 'index',
                width: 75,
            },
            ...columns.filter((column) => (
                column.field !== '__row_index__'
                && column.field !== '__duplicate_toggle__'
                && column.field !== GRID_DETAIL_PANEL_TOGGLE_FIELD
            )),
        ];
    }, [columns]);

    useEffect(() => {
        // console.log('selectedStatement:', selectedStatement)
        // Reset expanded duplicated panels whenever statement filter changes.
        setDetailPanelExpandedRowIds(new Set());
    }, [selectedStatement]);

    useEffect(() => {
        if (!isDuplicatedViewEnabled) {
            setDetailPanelExpandedRowIds(new Set());
            return;
        }

        const visibleIds = new Set(filteredData.map((row) => Number(row.id)));
        setDetailPanelExpandedRowIds((prev) => {
            const next = new Set();
            prev.forEach((id) => {
                if (visibleIds.has(id) && duplicatedRowIdSet.has(id)) {
                    next.add(id);
                }
            });
            return next;
        });
    }, [filteredData, duplicatedRowIdSet, isDuplicatedViewEnabled]);

    const handleLabelNoData = () => {
        // console.log('dataSourceSimulate:', dataSourceSimulate)
        // console.log('statementOptions:', statementOptions)
        const hasSimulationData = dataSourceSimulate?.data !== undefined;

        if (hasSimulationData && totalRows > 0 && statementOptions.length > 0) {
            setNoData(false);
            setAccessDenied(false);
            setHasData(true);
            return;
        } else if (hasSimulationData && totalRows === 0 && statementOptions.length > 0) {
            setNoData(true);
            setAccessDenied(false);
            setHasData(false);
            return;
        } else if (hasSimulationData && totalRows === 0 && statementOptions.length === 0) {
            setNoData(false);
            setAccessDenied(true);
            setHasData(false);
            return;
        } else {
            setNoData(false);
            setAccessDenied(false);
            setHasData(false);
            return;
        }
    };

    useEffect(() => {
        // console.log('dataSourceSimulate:', dataSourceSimulate)
        // console.log('statementOptions:', statementOptions)
        if (dataSourceSimulate && statementOptions) {
            handleLabelNoData();
        } else if (dataSourceSimulate === null && statementOptions.length === 0) {
            handleLabelNoData();
        }
    }, [dataSourceSimulate, statementOptions, totalRows])

    // useEffect(() => {
    //     if (filteredData.length === 0) {
    //         handleLabelNoData();
    //     }
    // }, [filteredData]);

    function CustomToolbar({ executeTime }) {
        return (
            <GridToolbarContainer
                sx={{
                    padding: '0px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', height: '28px', ml: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {(filteredData.length).toLocaleString()} rows
                    </Typography>
                    <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        Success in: {Number(executeTime).toLocaleString()} ms.
                    </Typography>
                    {(isDuplicatedViewEnabled && duplicatedEntries.length > 0) && (
                        <>
                            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title="Warning: Duplicated row detected" arrow placement="bottom">
                                <WarningIcon color="warning" sx={{ fontSize: '16px', transform: 'translateY(-1px)' }} />
                            </Tooltip>
                            {duplicatedEntries.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                                    {/* Duplicated rows: {duplicatedEntries.length.toLocaleString()} */}
                                    Duplicated rows: {normalizeCountDuplicated(duplicatedEntries).toLocaleString()}
                                </Typography>
                            )}
                        </>
                    )}
                    {(isDuplicatedViewEnabled && overlapDuplicatedEntries.length > 0) && (
                        <>
                            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title="Warning: Duplicated row detected" arrow placement="bottom">
                                <WarningIcon color="warning" sx={{ fontSize: '16px', transform: 'translateY(-1px)' }} />
                            </Tooltip>
                            {overlapDuplicatedEntries.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                                    Conflict rows: {overlapDuplicatedEntries.length.toLocaleString()}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', height: '28px' }}>
                    <Tooltip title="Columns">
                        <ColumnsPanelTrigger render={<ToolbarButton />}>
                            <ViewColumnIcon fontSize="small" />
                        </ColumnsPanelTrigger>
                    </Tooltip>

                    <Tooltip title="Filters">
                        <FilterPanelTrigger
                            render={(props, state) => (
                                <ToolbarButton {...props} color="default">
                                    <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                                        <FilterListIcon fontSize="small" />
                                    </Badge>
                                </ToolbarButton>
                            )}
                        />
                    </Tooltip>

                    <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

                    <StyledQuickFilter>
                        <QuickFilterTrigger
                            render={(triggerProps, state) => (
                                <Tooltip title="Search" enterDelay={0}>
                                    <StyledToolbarButton
                                        {...triggerProps}
                                        ownerState={{ expanded: state.expanded }}
                                        color="default"
                                        aria-disabled={state.expanded}
                                    >
                                        <SearchIcon fontSize="small" />
                                    </StyledToolbarButton>
                                </Tooltip>
                            )}
                        />
                        <QuickFilterControl
                            render={({ ref, ...controlProps }, state) => (
                                <StyledTextField
                                    {...controlProps}
                                    ownerState={{ expanded: state.expanded }}
                                    inputRef={ref}
                                    aria-label="Search"
                                    placeholder="Search..."
                                    size="small"
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: state.value ? (
                                                <InputAdornment position="end">
                                                    <QuickFilterClear
                                                        edge="end"
                                                        size="small"
                                                        aria-label="Clear search"
                                                        material={{ sx: { marginRight: -0.75 } }}
                                                    >
                                                        <CancelIcon fontSize="small" />
                                                    </QuickFilterClear>
                                                </InputAdornment>
                                            ) : null,
                                            ...controlProps.slotProps?.input,
                                        },
                                        ...controlProps.slotProps,
                                    }}
                                />
                            )}
                        />
                    </StyledQuickFilter>
                </Box>

            </GridToolbarContainer>
        );
    }

    return (
        <>
            {noData && (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <Typography>No data found for this user profile.</Typography>
                </Box>
            )}
            {accessDenied && (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <Typography>Access denied for this user profile.</Typography>
                </Box>
            )}
            {!hasData && (
                <></>
            )}
            {(!noData && !accessDenied && hasData) && (
                <Box sx={{
                    height: '100%',
                    width: '100%',
                    [`.${gridClasses.cell}.warning`]: {
                        backgroundColor: theme.palette.warning.main,
                    },
                    '& .MuiDataGrid-detailPanel': {
                        backgroundColor: '#fff7eb',
                    },
                }}>
                    <DataGridPremium
                        loading={isLoadingTable}
                        rows={filteredData}
                        columns={columns}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    id: false, // กำหนดให้ field 'id' เป็น false เพื่อซ่อน
                                    [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
                                },
                            },
                        }}
                        disableRowSelectionOnClick
                        disableDensitySelector
                        hideFooter={true}
                        slots={{
                            loadingOverlay: LinearProgress,
                            toolbar: CustomToolbar,
                            detailPanelExpandIcon: KeyboardArrowRightIcon,
                            detailPanelCollapseIcon: KeyboardArrowDownIcon,
                        }}
                        slotProps={{
                            toolbar: {
                                executeTime: executeTime
                            },
                        }}
                        rowHeight={30}
                        columnHeaderHeight={35}
                        sx={{
                            borderRadius: 0,
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 'bold',
                            },
                            '& .MuiDataGrid-toolbarContainer': {
                                height: '40px',
                                padding: '4px',
                            }
                        }}
                        showToolbar
                        disablePivoting
                        detailPanelExpandedRowIds={isDuplicatedViewEnabled ? detailPanelExpandedRowIds : EMPTY_EXPANDED_ROW_IDS}
                        onDetailPanelExpandedRowIdsChange={(newExpandedRowIds) => {
                            if (!isDuplicatedViewEnabled) {
                                setDetailPanelExpandedRowIds(new Set());
                                return;
                            }

                            const next = new Set();
                            if (newExpandedRowIds && typeof newExpandedRowIds.forEach === 'function') {
                                newExpandedRowIds.forEach((id) => {
                                    const numericId = Number(id);
                                    if (duplicatedRowIdSet.has(numericId)) {
                                        next.add(numericId);
                                    }
                                });
                            }
                            setDetailPanelExpandedRowIds(next);
                        }}
                        getDetailPanelHeight={({ id }) => {
                            if (!isDuplicatedViewEnabled) {
                                return 0;
                            }

                            const rowId = Number(id);
                            const duplicatePeers = duplicatedIndexMap.get(rowId);
                            const overlapDuplicatePeers = overlapDuplicatedIndexMap.get(rowId);
                            const duplicateRowsCount = duplicatePeers?.length || 0;
                            const overlapDuplicateRowsCount = overlapDuplicatePeers?.length || 0;
                            const sectionCount = (duplicateRowsCount > 0 ? 1 : 0) + (overlapDuplicateRowsCount > 0 ? 1 : 0);

                            if (sectionCount === 0) {
                                return 0;
                            }

                            const relatedRowsCount = duplicateRowsCount + overlapDuplicateRowsCount;
                            const estimatedPanelHeight = duplicatedDetailPanelBasePadding
                                + (sectionCount * duplicatedDetailSectionTitleHeight)
                                + (sectionCount * duplicatedDetailHeaderHeight)
                                + (relatedRowsCount * duplicatedDetailRowHeight)
                                + (Math.max(0, sectionCount - 1) * duplicatedDetailSectionSpacing);

                            return Math.min(
                                duplicatedDetailPanelMaxHeight,
                                Math.max(duplicatedDetailPanelMinHeight, estimatedPanelHeight)
                            );
                        }}
                        getDetailPanelContent={({ id }) => {
                            if (!isDuplicatedViewEnabled) {
                                return null;
                            }

                            const rowId = Number(id);
                            const duplicatePeers = duplicatedIndexMap.get(rowId);
                            const overlapDuplicatePeers = overlapDuplicatedIndexMap.get(rowId);

                            const duplicateRelatedIds = [...new Set(duplicatePeers || [])].sort((a, b) => a - b);
                            const overlapRelatedIds = [...new Set(overlapDuplicatePeers || [])].sort((a, b) => a - b);

                            if (duplicateRelatedIds.length === 0 && overlapRelatedIds.length === 0) {
                                return null;
                            }

                            const createDetailRows = (relatedIds, type) => {
                                return relatedIds
                                    .map((relatedId) => rowsWithIdMap.get(relatedId))
                                    .filter(Boolean)
                                    .map((row) => ({
                                        ...row,
                                        id: `detail-${type}-${rowId}-${row.id}`,
                                        __source_index__: Number(row.id),
                                    }));
                            };

                            const duplicateDetailRows = createDetailRows(duplicateRelatedIds, 'duplicated');
                            const overlapDetailRows = createDetailRows(overlapRelatedIds, 'conflict');

                            return (
                                <Box sx={{ p: 1, height: '100%', overflow: 'auto', pl: '50px' }}>
                                    {duplicateDetailRows.length > 0 && (
                                        <Box sx={{ mb: overlapDetailRows.length > 0 ? 1.5 : 0 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                                Duplicated
                                            </Typography>
                                            <DataGridPremium
                                                rows={duplicateDetailRows}
                                                columns={detailPanelColumns}
                                                hideFooter
                                                autoHeight
                                                disableRowSelectionOnClick
                                                density="compact"
                                                rowHeight={duplicatedDetailRowHeight}
                                                columnHeaderHeight={duplicatedDetailHeaderHeight}
                                                sx={{
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    backgroundColor: theme.palette.background.paper,
                                                    '& .MuiDataGrid-columnHeaderTitle': {
                                                        fontWeight: 600,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {overlapDetailRows.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                                Conflict
                                            </Typography>
                                            <DataGridPremium
                                                rows={overlapDetailRows}
                                                columns={detailPanelColumns}
                                                hideFooter
                                                autoHeight
                                                disableRowSelectionOnClick
                                                density="compact"
                                                rowHeight={duplicatedDetailRowHeight}
                                                columnHeaderHeight={duplicatedDetailHeaderHeight}
                                                sx={{
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    backgroundColor: theme.palette.background.paper,
                                                    '& .MuiDataGrid-columnHeaderTitle': {
                                                        fontWeight: 600,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            );
                        }}
                    // getCellClassName={(params) => {
                    //     if (!isDuplicatedViewEnabled) {
                    //         return '';
                    //     }

                    //     const highlightedIndexes = Array.isArray(highlightIds?.index) ? highlightIds.index : [];
                    //     const highlightedColumns = Array.isArray(highlightIds?.columns) ? highlightIds.columns : [];
                    //     const rowId = Number(params.id);

                    //     return (highlightedIndexes.length * highlightedColumns.length > 0)
                    //         ? ((highlightedIndexes.includes(rowId) && highlightedColumns.includes(params.field)) ? 'warning' : '')
                    //         : (highlightedIndexes.includes(rowId) ? 'warning' : '')
                    // }}
                    />
                </Box>
            )}
        </>
    );
}

export default DataGridSimulate;